import React, { useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  TextField,
  IconButton,
  CircularProgress,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./styles.module.css";

const IconeCompoe = ({ ativo, mostrarSpinner }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.5,
    }}
  >
    {ativo ? (
      <CheckIcon sx={{ color: "success.main", fontSize: 20 }} aria-label="Compõe" />
    ) : (
      <CloseIcon sx={{ color: "error.main", fontSize: 20 }} aria-label="Não compõe" />
    )}
    {mostrarSpinner && (
      <CircularProgress size={14} thickness={5} aria-label="Salvando" sx={{ flexShrink: 0 }} />
    )}
  </Box>
);

const TabelaGrupos = ({
  grupos,
  linhas,
  onVerDetalhes,
  onAtualizarGrupo,
  onAtualizarLinha,
  loading,
  gruposSalvando = new Set(),
  basesLegais = [],
  loadingBasesLegais = false,
  modoDetalhado = false,
  gruposInvalidos = [],
  lancamentosInvalidos = [], // Array com { contaContabil, linhaIndex }
  lancamentosFiltrados = new Set(), // Set de linhaIndex dos lançamentos filtrados por descrição
  isBloqueada = false,
  // ✅ PROPS PARA DRILL DOWN
  contaExpandida = null,
  onToggleExpand,
  lancamentosDetalhados = {},
  carregandoLancamentos = new Set(),
  onCarregarMais,
  colunaFiltro = "", // Prop para saber se há filtro de descrição ativo
  versaoIdPagina = undefined, // ID da versão da página (Sob Despesa): só marcar compõe/base legal quando id_versao do lançamento === versaoIdPagina
  bloqueioSalvamentoGrupoListaFechada = false,
  mostrarColunaTipoConta = true,
}) => {
  const scrollContainerRef = useRef({});
  const observerRef = useRef({});

  const formatarData = (data) => {
    if (!data) return "-";
    try {
      // Extrair data sem depender de timezone
      let dataFormatada;
      
      if (data instanceof Date) {
        const ano = data.getFullYear();
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const dia = String(data.getDate()).padStart(2, '0');
        dataFormatada = `${dia}/${mes}/${ano}`;
      } else if (typeof data === 'string') {
        // Tentar extrair YYYY-MM-DD diretamente
        const match = data.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
          dataFormatada = `${match[3]}/${match[2]}/${match[1]}`;
        } else {
          // Parsear como Date e extrair localmente
          const dateObj = new Date(data);
          if (!isNaN(dateObj.getTime())) {
            const ano = dateObj.getFullYear();
            const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
            const dia = String(dateObj.getDate()).padStart(2, '0');
            dataFormatada = `${dia}/${mes}/${ano}`;
          } else {
            return data;
          }
        }
      } else {
        return data;
      }
      
      return dataFormatada;
    } catch {
      return data;
    }
  };

  /** Tipo da conta contábil (contas_contabeis.tipo); fallback classificação por dígito (tipoConta). */
  const rotuloTipoConta = (item) => {
    const plano = item?.tipoContaContabil ?? item?.tipoEcd ?? item?.tipo_ecd;
    if (plano != null && String(plano).trim() !== "") return String(plano).trim();
    return item?.tipoConta ?? item?.tipo_conta ?? "-";
  };

  const formatarMoeda = (valor = 0) => {
    const num = typeof valor === "number" ? valor : parseFloat(valor) || 0;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(num);
  };

  const formatarPercentual = (valor = 0) => {
    const num = typeof valor === "number" ? valor : parseFloat(valor) || 0;
    return `${new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(num)} %`;
  };

  // Extrair código da conta da descrição (formato: "123 - Descrição")
  const extrairCodigoConta = (descricaoConta) => {
    if (!descricaoConta) return "";
    const match = descricaoConta.match(/^(\d+)\s*-/);
    return match ? match[1] : "";
  };

  // Normalizar dados
  const normalizarDados = () => {
    if (modoDetalhado && linhas) {
      return linhas.map((linha) => ({
        contaContabil: linha["Conta Contábil ECD"] || "-",
        descricaoConta: linha["Descrição Conta Contábil ECD"] || "-",
        codigoEcd: linha["Conta Contábil ECD"] ?? "-",
        valorTotalEcd: linha["Vl. Total ECD__number"] || linha.valorTotalEcd || 0,
        taxaSelic: linha["Taxa Selic__number"] ?? linha.taxaSelic ?? 0,
        valorEcdSelic: linha["Ecd Selic__number"] ?? linha.valorEcdSelic ?? linha["Vl. Total ECD__number"] ?? 0,
        vlTotalEcdContabil: linha["Vl. Total ECD__number"] ?? linha.vlTotalEcdContabil ?? 0,
        pis: linha["PIS__number"] || linha.pis || 0,
        cofins: linha["COFINS__number"] || linha.cofins || 0,
        baseLegalId: linha.baseLegalId || null,
        compoeProjeto: linha["Compõe Projeto__boolean"] || linha.compoeProjeto || false,
        tipoConta:
          linha.tipoContaContabil ??
          linha.tipoEcd ??
          linha.tipo_ecd ??
          linha.tipoConta ??
          linha["Tipo de Conta"] ??
          linha["Tipo da Conta"] ??
          linha["Tipo da Conta__string"] ??
          "-",
        _linhaIndex: linha._linhaIndex,
      }));
    }
    return (grupos || []).map((g) => ({ ...g, codigoEcd: g.codigoEcd ?? "-" }));
  };

  const dadosExibidos = normalizarDados();
  const numColunas = mostrarColunaTipoConta ? 10 : 9;

  // Configurar Intersection Observer para scroll infinito
  useEffect(() => {
    if (!contaExpandida) {
      // Limpar observers quando não há conta expandida
      Object.values(observerRef.current).forEach(obs => obs?.disconnect());
      observerRef.current = {};
      return;
    }

    const dados = lancamentosDetalhados[contaExpandida];
    if (!dados || !dados.temMais || carregandoLancamentos.has(contaExpandida)) {
      return;
    }

    const container = scrollContainerRef.current[contaExpandida];
    if (!container) return;

    // Limpar observer anterior para esta conta
    if (observerRef.current[contaExpandida]) {
      observerRef.current[contaExpandida].disconnect();
    }

    // Criar novo observer
    observerRef.current[contaExpandida] = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && dados.temMais && !carregandoLancamentos.has(contaExpandida)) {
          onCarregarMais?.(contaExpandida);
        }
      },
      { threshold: 0.8 }
    );

    // Observar o último elemento visível
    const ultimoElemento = container.querySelector(`.${styles.lancamentoRow}:last-child`);
    if (ultimoElemento) {
      observerRef.current[contaExpandida].observe(ultimoElemento);
    }

    return () => {
      if (observerRef.current[contaExpandida]) {
        observerRef.current[contaExpandida].disconnect();
      }
    };
  }, [contaExpandida, lancamentosDetalhados, carregandoLancamentos, onCarregarMais]);

  return (
    <div className={styles.tabelaContainer}>
      <TableContainer component={Paper} className={styles.tableWrapperGrupos}>
        <Table size="small">
          <TableHead>
            <TableRow className={styles.tableHeader}>
              <TableCell className={styles.colDescricaoConta}>Descrição da Conta</TableCell>
              {mostrarColunaTipoConta && (
                <TableCell className={styles.colTipoConta}>Tipo de Conta</TableCell>
              )}
              <TableCell className={styles.colCodigoEcd}>Código ECD</TableCell>
              <TableCell align="right" className={styles.colValorTotal}>Vl. contábil ECD</TableCell>
              <TableCell align="right" className={styles.colValorTotal}>Taxa SELIC</TableCell>
              <TableCell align="right" className={styles.colValorTotal}>ECD Selic</TableCell>
              <TableCell align="right" className={styles.colPis}>PIS</TableCell>
              <TableCell align="right" className={styles.colCofins}>COFINS</TableCell>
              <TableCell align="center" className={styles.colBaseLegal}>Base Legal</TableCell>
              <TableCell align="center" className={styles.colCompoe}>Compõe</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dadosExibidos.length === 0 && (
              <TableRow>
                <TableCell colSpan={numColunas} align="center" className={styles.emptyState}>
                  {loading ? "Carregando..." : "Nenhum registro encontrado para os filtros atuais."}
                </TableCell>
              </TableRow>
            )}

            {dadosExibidos.map((item, index) => {
              const baseLegalSelecionada =
                basesLegais.find((b) => b.id === item.baseLegalId) || null;
              
              const contaContabilItem = item?.contaContabil;
              // gruposInvalidos é um array de strings (contaContabil), não de objetos
              const isInvalido = gruposInvalidos && gruposInvalidos.length > 0 && 
                gruposInvalidos.some((contaInvalida) => {
                  if (!contaContabilItem || !contaInvalida) return false;
                  return String(contaInvalida).trim() === String(contaContabilItem).trim();
                });

              // Chave única do grupo (schema + id_conta) para consistência com SobDespesas
              const chaveGrupoItem = (item.schema_empresa != null && item.id_conta_contabil != null)
                ? `${item.schema_empresa}|${item.id_conta_contabil}`
                : item.contaContabil;
              const isSalvando = gruposSalvando instanceof Set && gruposSalvando.has(chaveGrupoItem);
              const bloqueioListaFechada = Boolean(bloqueioSalvamentoGrupoListaFechada && !modoDetalhado);
              const isDisabled = loading || isSalvando || isBloqueada || bloqueioListaFechada;
              
              // Verificar se esta conta está expandida (chave = schema|id para drill-down por schema+conta)
              const estaExpandida = contaExpandida === chaveGrupoItem;
              const codigoConta = extrairCodigoConta(item.descricaoConta);
              const dadosLancamentos = lancamentosDetalhados[chaveGrupoItem];
              const lancamentos = dadosLancamentos?.lancamentos || [];
              const estaCarregando = carregandoLancamentos.has(chaveGrupoItem);

              return (
                <React.Fragment key={`${chaveGrupoItem}-${index}`}>
                  {/* Linha do grupo */}
                  <TableRow 
                    className={`${styles.tableRow} ${isInvalido ? styles.invalidRow : ''} ${!modoDetalhado && estaExpandida ? styles.expandedRow : ''}`}
                    style={{ cursor: 'default' }}
                  >
                    <TableCell className={styles.colDescricaoConta}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {!modoDetalhado && (
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              onToggleExpand?.(item);
                            }}
                            disabled={isDisabled}
                            sx={{ 
                              padding: '4px',
                              color: '#18316e',
                              '&:hover': { backgroundColor: 'rgba(24, 49, 110, 0.1)' }
                            }}
                          >
                            {estaExpandida ? 
                              <ExpandLessIcon fontSize="small" /> : 
                              <ExpandMoreIcon fontSize="small" />
                            }
                          </IconButton>
                        )}
                        <span>
                          {codigoConta && <strong>{codigoConta} - </strong>}
                          {item.descricaoConta || "-"}
                        </span>
                      </div>
                    </TableCell>
                    {mostrarColunaTipoConta && (
                      <TableCell className={styles.colTipoConta}>
                        {rotuloTipoConta(item)}
                      </TableCell>
                    )}
                    <TableCell className={styles.colCodigoEcd}>
                      {item.codigoEcd ?? "-"}
                    </TableCell>
                    <TableCell align="right" className={styles.colValorTotal}>
                      {formatarMoeda(item.vlTotalEcdContabil ?? item.valorContabilEcd ?? item.valorTotalEcd)}
                    </TableCell>
                    <TableCell align="right" className={styles.colValorTotal}>
                      {formatarPercentual(item.taxaSelic ?? item.taxa_selic ?? 0)}
                    </TableCell>
                    <TableCell align="right" className={styles.colValorTotal}>
                      {formatarMoeda(item.valorEcdSelic ?? item.valor_ecd_selic ?? item.valorTotalEcd)}
                    </TableCell>
                    <TableCell align="right" className={styles.colPis}>
                      {formatarMoeda(item.pis)}
                    </TableCell>
                    <TableCell align="right" className={styles.colCofins}>
                      {formatarMoeda(item.cofins)}
                    </TableCell>
                    <TableCell align="center" className={styles.colBaseLegal}>
                      <Autocomplete
                        size="small"
                        options={basesLegais}
                        getOptionLabel={(option) => option.descricao || ""}
                        value={baseLegalSelecionada}
                        onChange={(_, novoValor) => {
                          const baseLegalId = novoValor?.id ?? null;
                          if (modoDetalhado && onAtualizarLinha) {
                            onAtualizarLinha?.(item, baseLegalId);
                          } else {
                            onAtualizarGrupo?.(item, baseLegalId);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        loading={loadingBasesLegais}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Selecione" />
                        )}
                        className={styles.autocomplete}
                        disabled={isDisabled}
                      />
                    </TableCell>
                    <TableCell align="center" className={styles.colCompoe}>
                      <IconeCompoe
                        ativo={Boolean(item.compoeProjeto)}
                        mostrarSpinner={isSalvando && !modoDetalhado}
                      />
                    </TableCell>
                  </TableRow>

                  {/* Linhas expandidas com lançamentos */}
                  {!modoDetalhado && estaExpandida && (
                    <TableRow>
                      <TableCell colSpan={numColunas} style={{ padding: 0, border: 'none' }}>
                        <div 
                          ref={(el) => {
                            if (el) {
                              scrollContainerRef.current[chaveGrupoItem] = el;
                            }
                          }}
                          className={styles.lancamentosContainer}
                        >
                          {/* Cabeçalho das colunas dos lançamentos */}
                          <div className={styles.lancamentosHeader}>
                            <div className={styles.lancamentoCol}>Vl. contábil ECD</div>
                            <div className={styles.lancamentoCol}>Taxa SELIC</div>
                            <div className={styles.lancamentoCol}>ECD Selic</div>
                            <div className={styles.lancamentoCol}>PIS</div>
                            <div className={styles.lancamentoCol}>COFINS</div>
                            <div className={styles.lancamentoCol}>Descrição</div>
                            <div className={styles.lancamentoCol}>Dt. Lanç.</div>
                            <div className={styles.lancamentoCol}>Base Legal</div>
                            <div className={`${styles.lancamentoCol} ${styles.lancamentoColCompoe}`}>Compõe</div>
                          </div>

                          {/* Lista de lançamentos (máximo 7 visíveis com scroll) */}
                          <div className={styles.lancamentosList}>
                            {(() => {
                              // ✅ Filtrar lançamentos se houver filtro de descrição ativo
                              let lancamentosParaExibir = lancamentos;
                              
                              if (colunaFiltro === "descricaoLancamento" && lancamentosFiltrados instanceof Set && lancamentosFiltrados.size > 0) {
                                lancamentosParaExibir = lancamentos.filter((lancamento, idx) => {
                                  const linhaIndexLancamento = lancamento._linhaIndex !== undefined 
                                    ? lancamento._linhaIndex 
                                    : (lancamento.linhaIndex !== undefined ? lancamento.linhaIndex : idx);
                                  return lancamentosFiltrados.has(linhaIndexLancamento);
                                });
                              }

                              if (lancamentosParaExibir.length === 0 && !estaCarregando) {
                                return (
                                  <div className={styles.emptyLancamentos}>
                                    {colunaFiltro === "descricaoLancamento" 
                                      ? "Nenhum lançamento encontrado com a descrição informada"
                                      : "Nenhum lançamento encontrado"}
                                  </div>
                                );
                              }

                              return lancamentosParaExibir.map((lancamento, idx) => {
                              // Normalizar dados do lançamento
                              const vlContabil = lancamento["Vl. Total ECD__number"] || lancamento["Vl. Total ECD"] || lancamento.valorTotalEcd || 0;
                              const taxaSelicL = lancamento["Taxa Selic__number"] ?? lancamento.taxaSelic ?? 0;
                              const ecdSelicL = lancamento["Ecd Selic__number"] ?? lancamento.valorEcdSelic ?? vlContabil;
                              const pis = lancamento["PIS__number"] || lancamento["PIS"] || lancamento.pis || 0;
                              const cofins = lancamento["COFINS__number"] || lancamento["COFINS"] || lancamento.cofins || 0;
                              const descricao = lancamento["Descrição"] || lancamento["Descrição Conta Contábil ECD"] || lancamento["Descrição__string"] || lancamento.descricao || "-";
                              const dtLancamento = lancamento["Dt. Lanç.__date"] || lancamento["Dt. Lançamento ECD__date"] || lancamento["Dt. Lanç.__string"] || lancamento.dtLancamentoEcd || null;
                              const compoeProjeto = lancamento["Compõe Projeto__boolean"] || lancamento["Compõe Projeto"] || lancamento.compoeProjeto || false;
                              const baseLegalId = lancamento.baseLegalId || null;
                              const idVersao = lancamento.idVersao ?? lancamento.versaoId ?? null;
                              // Só marcar compõe/base legal quando a versão do lançamento é a mesma da página
                              const mesmaVersao = versaoIdPagina != null && idVersao != null && Number(idVersao) === Number(versaoIdPagina);
                              const compoeProjetoExibir = mesmaVersao ? compoeProjeto : false;
                              const baseLegalIdExibir = mesmaVersao ? baseLegalId : null;
                              const baseLegalSelecionadaLanc = basesLegais.find((b) => b.id === baseLegalIdExibir) || null;

                              // Verificar se este lançamento específico está inválido
                              // O backend retorna linhaIndex, mas os lançamentos carregados têm _linhaIndex
                              const linhaIndexLancamento = lancamento._linhaIndex !== undefined ? lancamento._linhaIndex : (lancamento.linhaIndex !== undefined ? lancamento.linhaIndex : idx);
                              const isLancamentoInvalido = lancamentosInvalidos && lancamentosInvalidos.length > 0 &&
                                lancamentosInvalidos.some(li => {
                                  // Normalizar conta contábil para comparação (pode vir em diferentes formatos)
                                  const contaMatch = String(li.contaContabil || '').trim() === String(item.contaContabil || '').trim();
                                  // Comparar linhaIndex (backend) com _linhaIndex (lançamentos carregados)
                                  const linhaMatch = li.linhaIndex === linhaIndexLancamento;
                                  return contaMatch && linhaMatch;
                                });
                              
                              return (
                                <div 
                                  key={`lancamento-${linhaIndexLancamento}`}
                                  className={`${styles.lancamentoRow} ${isLancamentoInvalido ? styles.invalidLancamentoRow : ''}`}
                                >
                                  <div className={styles.lancamentoCol} style={{ textAlign: 'right' }}>
                                    {formatarMoeda(vlContabil)}
                                  </div>
                                  <div className={styles.lancamentoCol} style={{ textAlign: 'right' }}>
                                    {formatarPercentual(taxaSelicL)}
                                  </div>
                                  <div className={styles.lancamentoCol} style={{ textAlign: 'right' }}>
                                    {formatarMoeda(ecdSelicL)}
                                  </div>
                                  <div className={styles.lancamentoCol} style={{ textAlign: 'right' }}>
                                    {formatarMoeda(pis)}
                                  </div>
                                  <div className={styles.lancamentoCol} style={{ textAlign: 'right' }}>
                                    {formatarMoeda(cofins)}
                                  </div>
                                  <div className={`${styles.lancamentoCol} ${styles.descricaoCol}`}>
                                    {descricao}
                                  </div>
                                  <div className={styles.lancamentoCol}>
                                    {formatarData(dtLancamento)}
                                  </div>
                                  <div className={styles.lancamentoCol}>
                                    <Autocomplete
                                      size="small"
                                      options={basesLegais}
                                      getOptionLabel={(option) => option.descricao || ""}
                                      value={baseLegalSelecionadaLanc}
                                      onChange={(_, novoValor) => {
                                        onAtualizarLinha?.(
                                          { ...lancamento, contaContabil: item.contaContabil },
                                          novoValor?.id ?? null
                                        );
                                      }}
                                      loading={loadingBasesLegais}
                                      renderInput={(params) => (
                                        <TextField {...params} placeholder="Selecione" size="small" />
                                      )}
                                      disabled={isDisabled || !mesmaVersao}
                                      sx={{ minWidth: '150px' }}
                                    />
                                  </div>
                                  <div className={`${styles.lancamentoCol} ${styles.lancamentoColCompoe}`}>
                                    <IconeCompoe ativo={Boolean(compoeProjetoExibir)} mostrarSpinner={false} />
                                  </div>
                                </div>
                              );
                            });
                            })()}

                            {/* Indicador de loading */}
                            {estaCarregando && (
                              <div className={styles.loadingLancamentos}>
                                <CircularProgress size={20} />
                                <span>Carregando lançamentos...</span>
                              </div>
                            )}

                            {/* Mensagem de fim */}
                            {!estaCarregando && dadosLancamentos && !dadosLancamentos.temMais && lancamentos.length > 0 && (
                              <div className={styles.fimLancamentos}>
                                Fim dos lançamentos ({dadosLancamentos.total || lancamentos.length} registro{dadosLancamentos.total !== 1 ? 's' : ''})
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default TabelaGrupos;
